import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Search, Package, Building, FileText, MapPin, ChevronRight } from 'lucide-react';
import { searchApi } from '../services/api';
import { Link } from 'react-router-dom';

const SearchResultsPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState(searchParams.get('q') || '');
  const [type, setType] = useState(searchParams.get('type') || 'all');
  const [results, setResults] = useState({ products: [], suppliers: [], rfqs: [] });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const q = searchParams.get('q');
    const t = searchParams.get('type') || 'all';
    if (q) {
      setQuery(q);
      setType(t);
      performSearch(q, t);
    }
  }, [searchParams]);

  const performSearch = async (q, t) => {
    if (!q.trim()) return;
    setLoading(true);
    try {
      const { data } = await searchApi.global({ q, type: t });
      setResults(data);
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (query.trim()) {
      setSearchParams({ q: query, type });
    }
  };

  const totalResults = (results.products_total || 0) + (results.suppliers_total || 0) + (results.rfqs_total || 0);

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <Header />

      {/* Search Header */}
      <section className="bg-white border-b">
        <div className="container-main py-8">
          <form onSubmit={handleSearch} className="max-w-3xl mx-auto">
            <div className="flex gap-2">
              <div className="flex-1 relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search products, suppliers, or buy offers..."
                  className="pl-12 h-12 text-lg"
                  data-testid="search-input"
                />
              </div>
              <Button type="submit" className="h-12 px-8 bg-[#EA580C] hover:bg-[#C2410C]" data-testid="search-btn">
                Search
              </Button>
            </div>
          </form>
        </div>
      </section>

      <div className="container-main py-8">
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-slate-200 border-t-blue-600 mx-auto mb-4"></div>
            <p className="text-slate-600">Searching...</p>
          </div>
        ) : searchParams.get('q') ? (
          <>
            <p className="text-slate-600 mb-6">
              Found <span className="font-semibold text-slate-900">{totalResults}</span> results for "{searchParams.get('q')}"
            </p>

            <Tabs value={type} onValueChange={(t) => setSearchParams({ q: query, type: t })}>
              <TabsList className="bg-white border mb-6">
                <TabsTrigger value="all" data-testid="search-tab-all">
                  All ({totalResults})
                </TabsTrigger>
                <TabsTrigger value="products" data-testid="search-tab-products">
                  Products ({results.products_total || 0})
                </TabsTrigger>
                <TabsTrigger value="suppliers" data-testid="search-tab-suppliers">
                  Suppliers ({results.suppliers_total || 0})
                </TabsTrigger>
                <TabsTrigger value="rfqs" data-testid="search-tab-rfqs">
                  Buy Offers ({results.rfqs_total || 0})
                </TabsTrigger>
              </TabsList>

              {/* All Results */}
              <TabsContent value="all" className="space-y-8">
                {/* Products Section */}
                {results.products?.length > 0 && (
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                        <Package className="w-5 h-5 text-[#2563EB]" />
                        Products
                      </h2>
                      <Button variant="link" onClick={() => setSearchParams({ q: query, type: 'products' })}>
                        View all <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {results.products.slice(0, 6).map((product) => (
                        <Link
                          key={product.product_id}
                          to={`/products/${product.product_id}`}
                          className="bg-white border rounded-lg p-4 hover:shadow-md transition-shadow flex gap-4"
                        >
                          <div className="w-20 h-20 bg-slate-100 rounded-lg overflow-hidden flex-shrink-0">
                            {product.images?.[0] ? (
                              <img src={product.images[0]} alt="" className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <Package className="w-8 h-8 text-slate-300" />
                              </div>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="font-medium text-slate-900 line-clamp-2">{product.title}</h3>
                            <p className="text-sm text-[#EA580C] font-semibold mt-1">
                              ${product.price_min?.toFixed(2)} - ${product.price_max?.toFixed(2)}
                            </p>
                            <p className="text-xs text-slate-500 mt-1">Verified Supplier</p>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}

                {/* Suppliers Section */}
                {results.suppliers?.length > 0 && (
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                        <Building className="w-5 h-5 text-[#2563EB]" />
                        Suppliers
                      </h2>
                      <Button variant="link" onClick={() => setSearchParams({ q: query, type: 'suppliers' })}>
                        View all <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {results.suppliers.slice(0, 6).map((supplier) => (
                        <Link
                          key={supplier.user_id}
                          to={`/suppliers/${supplier.user_id}`}
                          className="bg-white border rounded-lg p-4 hover:shadow-md transition-shadow"
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#2563EB] to-[#EA580C] flex items-center justify-center text-white font-bold">
                              {supplier.company_name?.charAt(0)}
                            </div>
                            <div>
                              <h3 className="font-medium text-slate-900">{supplier.company_name}</h3>
                              <p className="text-sm text-slate-500 flex items-center gap-1">
                                <MapPin className="w-3 h-3" />
                                {supplier.country || 'International'}
                              </p>
                            </div>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}

                {/* RFQs Section */}
                {results.rfqs?.length > 0 && (
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                        <FileText className="w-5 h-5 text-[#2563EB]" />
                        Buy Offers
                      </h2>
                      <Button variant="link" onClick={() => setSearchParams({ q: query, type: 'rfqs' })}>
                        View all <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                    <div className="space-y-4">
                      {results.rfqs.slice(0, 5).map((rfq) => (
                        <Link
                          key={rfq.rfq_id}
                          to={`/rfqs/${rfq.rfq_id}`}
                          className="block bg-white border rounded-lg p-4 hover:shadow-md transition-shadow"
                        >
                          <div className="flex items-start justify-between">
                            <div>
                              <h3 className="font-medium text-slate-900">{rfq.title}</h3>
                              <p className="text-sm text-slate-600 line-clamp-1 mt-1">{rfq.description}</p>
                              <div className="flex items-center gap-3 mt-2 text-sm text-slate-500">
                                <span>{rfq.quantity} {rfq.quantity_unit}</span>
                                <span>{rfq.destination_country}</span>
                                <Badge variant="outline">{rfq.category}</Badge>
                              </div>
                            </div>
                            {rfq.is_urgent && (
                              <Badge className="bg-red-50 text-red-700 border-red-200">Urgent</Badge>
                            )}
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}

                {totalResults === 0 && (
                  <div className="text-center py-12">
                    <Search className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-slate-900 mb-2">No results found</h3>
                    <p className="text-slate-600">Try different keywords or browse our categories</p>
                  </div>
                )}
              </TabsContent>

              {/* Products Tab */}
              <TabsContent value="products">
                {results.products?.length > 0 ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {results.products.map((product) => (
                      <Link
                        key={product.product_id}
                        to={`/products/${product.product_id}`}
                        className="bg-white border rounded-xl overflow-hidden hover:shadow-lg transition-shadow"
                      >
                        <div className="h-48 bg-slate-100">
                          {product.images?.[0] ? (
                            <img src={product.images[0]} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Package className="w-12 h-12 text-slate-300" />
                            </div>
                          )}
                        </div>
                        <div className="p-4">
                          <h3 className="font-semibold text-slate-900 line-clamp-2">{product.title}</h3>
                          <p className="text-[#EA580C] font-bold mt-2">
                            ${product.price_min?.toFixed(2)} - ${product.price_max?.toFixed(2)}
                          </p>
                          <p className="text-sm text-slate-500 mt-1">
                            MOQ: {product.moq} {product.moq_unit}
                          </p>
                        </div>
                      </Link>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Package className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-600">No products found</p>
                  </div>
                )}
              </TabsContent>

              {/* Suppliers Tab */}
              <TabsContent value="suppliers">
                {results.suppliers?.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {results.suppliers.map((supplier) => (
                      <Link
                        key={supplier.user_id}
                        to={`/suppliers/${supplier.user_id}`}
                        className="bg-white border rounded-xl p-6 text-center hover:shadow-lg transition-shadow"
                      >
                        <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#2563EB] to-[#EA580C] flex items-center justify-center mx-auto mb-4 text-white text-2xl font-bold">
                          {supplier.company_name?.charAt(0)}
                        </div>
                        <h3 className="font-semibold text-slate-900">{supplier.company_name}</h3>
                        <p className="text-sm text-slate-500">{supplier.country}</p>
                      </Link>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Building className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-600">No suppliers found</p>
                  </div>
                )}
              </TabsContent>

              {/* RFQs Tab */}
              <TabsContent value="rfqs">
                {results.rfqs?.length > 0 ? (
                  <div className="space-y-4">
                    {results.rfqs.map((rfq) => (
                      <Link
                        key={rfq.rfq_id}
                        to={`/rfqs/${rfq.rfq_id}`}
                        className="block bg-white border rounded-xl p-6 hover:shadow-lg transition-shadow"
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <h3 className="font-semibold text-lg text-slate-900">{rfq.title}</h3>
                            <p className="text-slate-600 mt-2">{rfq.description}</p>
                            <div className="flex items-center gap-4 mt-4 text-sm text-slate-500">
                              <span>{rfq.quantity} {rfq.quantity_unit}</span>
                              <span>{rfq.destination_country}</span>
                              <Badge variant="outline">{rfq.category}</Badge>
                            </div>
                          </div>
                          {rfq.is_urgent && (
                            <Badge className="bg-red-50 text-red-700 border-red-200">Urgent</Badge>
                          )}
                        </div>
                      </Link>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-600">No buy offers found</p>
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </>
        ) : (
          <div className="text-center py-12">
            <Search className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-900 mb-2">Start searching</h3>
            <p className="text-slate-600">Enter keywords to find products, suppliers, or buy offers</p>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default SearchResultsPage;
